/**
 * Shift/block timing rules shared by the admin frontend and the shifts edge function.
 * Kept free of Deno and DOM globals so that both Vite and Deno can import this file
 * directly. Its one dependency, `@date-fns/tz`, is imported with an `npm:` specifier
 * (aliased to the package in vite.config.ts and vitest.config.ts) for the same reason.
 */
// The version here must match package.json and the aliases in vite.config.ts and vitest.config.ts.
// @ts-expect-error npm: specifier, resolved by Deno natively and by the bundler aliases
import { TZDate } from "npm:@date-fns/tz@1.4.1"

/**
 * Grace period applied when rejecting blocks that start in the past, so a block
 * created for "right now" isn't rejected because of the time taken to submit the
 * form, request latency, or a few seconds of clock skew between the browser and
 * the edge runtime.
 */
export const SHIFT_START_PAST_TOLERANCE_MINUTES = 2

/**
 * Message shown by the dialogs and returned by the API when a start time has passed.
 * The unpunctuated DETAIL form is for contexts that add their own punctuation or
 * prefix, such as the per-row errors from a CSV upload.
 */
export const SHIFT_START_IN_PAST_DETAIL = "Block start time cannot be in the past"
export const SHIFT_START_IN_PAST_ERROR = `${SHIFT_START_IN_PAST_DETAIL}.`

/** API error code paired with SHIFT_START_IN_PAST_ERROR. */
export const SHIFT_START_IN_PAST_CODE = "ERROR_START_TIME_IN_PAST"

/**
 * True when `start` is far enough in the past to reject, allowing for the
 * tolerance above. Applies to block creation only — updates may legitimately
 * touch a block that has already started.
 */
export function isShiftStartInPast(start: Date, now: Date = new Date()): boolean {
  return start.getTime() < now.getTime() - SHIFT_START_PAST_TOLERANCE_MINUTES * 60_000
}

/**
 * Shift times are entered, and meant, as UK wall-clock times. Everything below works
 * from the date and time strings directly and never reads the runtime's own timezone,
 * so the admin UI (browser timezone) and edge functions (UTC) agree on the result.
 */
export const UK_TIMEZONE = "Europe/London"

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/
const TIME_PATTERN = /^(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/

/** Splits a strictly formatted HH:mm, HH:mm:ss or HH:mm:ss.SSS time, or null if it isn't one. */
function parseTime(
  time: string,
): { hour: number; minute: number; second: number; millisecond: number } | null {
  const match = TIME_PATTERN.exec(time)
  if (!match) return null
  const [, hour, minute, second = "0", millisecond = "0"] = match
  return {
    hour: Number(hour),
    minute: Number(minute),
    second: Number(second),
    millisecond: Number(millisecond.padEnd(3, "0")),
  }
}

/**
 * The UK calendar date of an instant, as YYYY-MM-DD. Unlike `toISOString().slice(0, 10)`,
 * this doesn't report the previous day between 00:00 and 01:00 during BST.
 */
export function ukDateOf(instant: Date): string {
  return ukIsoOf(instant).slice(0, 10)
}

/** The UK wall-clock time of an instant, as HH:mm. */
export function ukTimeOf(instant: Date): string {
  return ukIsoOf(instant).slice(11, 16)
}

/** The instant's ISO string in UK time: it carries the zone's offset, so its date and time are local. */
const ukIsoOf = (instant: Date): string => new TZDate(instant.getTime(), UK_TIMEZONE).toISOString()

/** Today's date in the UK as YYYY-MM-DD. */
export function ukToday(now: Date = new Date()): string {
  return ukDateOf(now)
}

/**
 * The UK date range a named metrics filter covers, both ends inclusive and weeks starting on
 * Sunday, or null for any other filter.
 */
export function ukDateFilterRange(
  filter: string,
  today: string = ukToday(),
): { startDate: string; endDate: string } | null {
  const firstOfMonth = `${today.slice(0, 7)}-01`
  switch (filter) {
    case "today":
      return { startDate: today, endDate: today }
    case "this_week": {
      // Read the weekday of the date string itself, in UTC, so no timezone can shift it.
      const weekday = new Date(`${today}T12:00:00Z`).getUTCDay()
      return { startDate: addDaysToDateString(today, -weekday), endDate: today }
    }
    case "this_month":
      return { startDate: firstOfMonth, endDate: today }
    case "last_month": {
      const endDate = addDaysToDateString(firstOfMonth, -1)
      return { startDate: `${endDate.slice(0, 7)}-01`, endDate }
    }
    default:
      return null
  }
}

/** Shown when a date/time can't be turned into a UK instant (see `ukWallClockToInstant`). */
export const INVALID_UK_TIME_ERROR =
  "Enter a valid date and time. UK times between 01:00 and 02:00 on the day clocks go forward don't exist."

/**
 * Converts a UK date (YYYY-MM-DD) and time (HH:mm, HH:mm:ss or HH:mm:ss.SSS) to the instant
 * it names.
 *
 * A time that happens twice (clocks go back) resolves to the first occurrence, in BST. A time
 * that never happens (clocks go forward), an invalid date, or any other format returns an
 * Invalid Date, so callers can reject it with the same `isNaN(date.getTime())` check as any
 * bad input.
 */
export function ukWallClockToInstant(date: string, time: string): Date {
  const dateMatch = DATE_PATTERN.exec(date)
  const parsedTime = parseTime(time)
  if (!dateMatch || !parsedTime) {
    return new Date(NaN)
  }
  const [year, month, day] = dateMatch.slice(1).map(Number)
  const { hour, minute, second, millisecond } = parsedTime

  // TZDate rolls impossible values over (30 Feb, 25:00) and moves a time skipped by the
  // clocks going forward onto the next valid one, so a changed reading means it was invalid.
  const ukTime = new TZDate(year, month - 1, day, hour, minute, second, millisecond, UK_TIMEZONE)
  if (
    ukTime.getFullYear() !== year ||
    ukTime.getMonth() !== month - 1 ||
    ukTime.getDate() !== day ||
    ukTime.getHours() !== hour ||
    ukTime.getMinutes() !== minute ||
    ukTime.getSeconds() !== second ||
    ukTime.getMilliseconds() !== millisecond
  ) {
    return new Date(NaN)
  }
  // A time that happens twice (clocks go back) is resolved by TZDate to whichever
  // occurrence suits the host's own timezone, so pick the first, in BST, ourselves.
  const hourEarlier = new TZDate(ukTime.getTime() - 3_600_000, UK_TIMEZONE)
  const isRepeated = hourEarlier.getDate() === day && hourEarlier.getHours() === hour
  return new Date(isRepeated ? hourEarlier.getTime() : ukTime.getTime())
}

/** Adds calendar days to a YYYY-MM-DD date string. */
export const addDaysToDateString = (date: string, days: number): string => {
  const [year, month, day] = date.split("-").map(Number)
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10)
}

/** The first instant of a UK calendar day (YYYY-MM-DD). */
export function ukDayStart(date: string): Date {
  return ukWallClockToInstant(date, "00:00")
}

/** The last millisecond of a UK calendar day (YYYY-MM-DD), 23 or 25 hours after its start on clock-change days. */
export function ukDayEnd(date: string): Date {
  return new Date(ukDayStart(addDaysToDateString(date, 1)).getTime() - 1)
}

/**
 * Every date from `startDate` to `endDate` inclusive, as YYYY-MM-DD. Empty if start is after end.
 * Throws a RangeError unless both are YYYY-MM-DD, since the loop compares them as strings.
 */
export function eachDateInRange(startDate: string, endDate: string): string[] {
  if (!DATE_PATTERN.test(startDate) || !DATE_PATTERN.test(endDate)) {
    throw new RangeError(`Expected YYYY-MM-DD dates, got "${startDate}" and "${endDate}"`)
  }
  const dates: string[] = []
  for (let date = startDate; date <= endDate; date = addDaysToDateString(date, 1)) {
    dates.push(date)
  }
  return dates
}

const timeOfDayMs = ({ hour, minute, second, millisecond }: ReturnType<typeof parseTime>) =>
  ((hour * 60 + minute) * 60 + second) * 1000 + millisecond

/**
 * The start and end instants of a block that starts on `date`. An end time at or before
 * the start time means the block ends the next day. Either instant is an Invalid Date if
 * that UK time doesn't exist (see `ukWallClockToInstant`).
 */
export function blockInstants({
  date,
  startTime,
  endTime,
}: {
  date: string
  startTime: string
  endTime: string
}): { start: Date; end: Date } {
  // Compare the parsed times, not the strings, so "17:00" and "17:00:00" count as equal.
  const start = parseTime(startTime)
  const end = parseTime(endTime)
  const endsNextDay = start && end && timeOfDayMs(end) <= timeOfDayMs(start)
  const endDate = endsNextDay ? addDaysToDateString(date, 1) : date
  return {
    start: ukWallClockToInstant(date, startTime),
    end: ukWallClockToInstant(endDate, endTime),
  }
}
